import * as THREE from "three";
import type { LightShaftPlacement } from "./lightShaftLayout";

/**
 * Geometry for the evening god rays (issue 076 follow-up). The shafts
 * used to be `ConeGeometry` with the *apex* pinned at the window, which
 * was wrong in three ways: the zero-area tip sat on the glass (so the
 * beam appeared to grow out of a point), the circular cross-section
 * collapsed the opening's aspect ratio, and the clamped radius decoupled
 * the beam from the opening entirely outside 0.36–1.2 m.
 *
 * The replacement is a hand-built four-sided frustum: the near quad is
 * *exactly* the opening rectangle (`widthM × heightM`, at `origin`,
 * squared to the facade), and the far quad is the same rectangle scaled
 * by {@link SHAFT_SPREAD} out along the facade normal. A hand-built
 * BufferGeometry beats `CylinderGeometry(rTop, rBottom, …, 4)` here
 * because the latter needs a 45° spin plus non-uniform scale to become
 * rectangular, and the scale then also skews the length axis.
 *
 * Pure + WebGL-free on purpose so the "does the base match the window"
 * question is answered by a unit test instead of a screenshot — the
 * shafts are tier-gated to `balanced`+ and the adaptive quality monitor
 * silently drops them under a headless GPU (see
 * docs/notes/2026-06-27-evening-god-rays-and-tier-gated-viewer-verification.md).
 */

/** How much wider/taller the far end is than the opening itself. */
export const SHAFT_SPREAD = 1.8;

/** Beam length as a multiple of the opening's largest dimension. */
const LENGTH_PER_SPAN = 2.2;
/** Length-only clamps — the *base* always tracks the opening exactly. */
const MIN_LENGTH_M = 1.2;
const MAX_LENGTH_M = 2.4;

const WORLD_UP = new THREE.Vector3(0, 1, 0);
const FALLBACK_TANGENT = new THREE.Vector3(1, 0, 0);

export interface LightShaftFrustum {
	/**
	 * Local-space frustum. Cross-section lives in XZ (X = wall tangent,
	 * Z = world up) at true metre extents; the length axis runs
	 * y = 0 (near quad, at the glass) → y = -1 (far quad), the unit range
	 * the shader's `vLengthFade` assumes. Scale the mesh's Y to stretch.
	 */
	geometry: THREE.BufferGeometry;
	/** Maps local -Y onto the facade normal and local +Z onto world up. */
	quaternion: THREE.Quaternion;
	/** Y-only stretch; X/Z stay 1 so the base keeps its baked metres. */
	scale: [number, number, number];
	lengthM: number;
}

/** Beam length for an opening: proportional, with sane min/max. */
export function getLightShaftLength(placement: LightShaftPlacement): number {
	const span = Math.max(placement.widthM, placement.heightM);
	return THREE.MathUtils.clamp(
		span * LENGTH_PER_SPAN,
		MIN_LENGTH_M,
		MAX_LENGTH_M,
	);
}

/**
 * Orientation basis for a facade: local +X = wall tangent, local -Y =
 * outward normal (the direction the beam travels), local +Z = world up.
 */
export function getLightShaftQuaternion(
	normalTuple: readonly [number, number, number],
): THREE.Quaternion {
	const normal = new THREE.Vector3(
		normalTuple[0],
		normalTuple[1],
		normalTuple[2],
	).normalize();

	const tangent = new THREE.Vector3().crossVectors(WORLD_UP, normal);
	// Degenerate only if a facade ever points straight up/down (roof
	// glazing); pick any perpendicular rather than emitting NaNs.
	if (tangent.lengthSq() < 1e-8) tangent.copy(FALLBACK_TANGENT);
	tangent.normalize();

	const lengthAxis = normal.clone().negate();
	const upAxis = new THREE.Vector3()
		.crossVectors(tangent, lengthAxis)
		.normalize();

	return new THREE.Quaternion().setFromRotationMatrix(
		new THREE.Matrix4().makeBasis(tangent, lengthAxis, upAxis),
	);
}

/**
 * Four side quads, open at both ends (no caps — a capped end would read
 * as a lid of solid light under additive blending). Vertices are shared
 * between adjacent sides and carry the averaged radial normal, so the
 * fresnel rim interpolates smoothly across each face and the shaft still
 * reads as soft volume rather than a hard box.
 */
export function buildLightShaftFrustum(
	placement: LightShaftPlacement,
): LightShaftFrustum {
	const halfW = placement.widthM / 2;
	const halfH = placement.heightM / 2;
	const farHalfW = halfW * SHAFT_SPREAD;
	const farHalfH = halfH * SHAFT_SPREAD;

	// Corner order runs (-x,-z) → (+x,-z) → (+x,+z) → (-x,+z) so near
	// index i and far index i + 4 are the same corner of the rectangle.
	const cornerSigns: Array<[number, number]> = [
		[-1, -1],
		[1, -1],
		[1, 1],
		[-1, 1],
	];

	const positions = new Float32Array(8 * 3);
	const normals = new Float32Array(8 * 3);
	const invSqrt2 = Math.SQRT1_2;

	cornerSigns.forEach(([sx, sz], i) => {
		positions[i * 3] = sx * halfW;
		positions[i * 3 + 1] = 0;
		positions[i * 3 + 2] = sz * halfH;

		const far = i + 4;
		positions[far * 3] = sx * farHalfW;
		positions[far * 3 + 1] = -1;
		positions[far * 3 + 2] = sz * farHalfH;

		// Average of the two side normals meeting at this corner. Aspect
		// independent (the side normals are ±X / ±Z whatever the shape)
		// and free of a Y component, so the mesh's Y stretch can't skew it.
		for (const v of [i, far]) {
			normals[v * 3] = sx * invSqrt2;
			normals[v * 3 + 1] = 0;
			normals[v * 3 + 2] = sz * invSqrt2;
		}
	});

	const indices: number[] = [];
	for (let i = 0; i < 4; i++) {
		const nearA = i;
		const nearB = (i + 1) % 4;
		const farA = nearA + 4;
		const farB = nearB + 4;
		indices.push(nearA, nearB, farB, nearA, farB, farA);
	}

	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
	geometry.setIndex(indices);

	const lengthM = getLightShaftLength(placement);

	return {
		geometry,
		quaternion: getLightShaftQuaternion(placement.normal),
		scale: [1, lengthM, 1],
		lengthM,
	};
}
